// eslint-disable-next-line import/no-cycle
import {
  AcquisitionChannel,
  BookingIssue,
  BookingPaymentBalanceStatus,
  BookingStatus,
  GuestReadinessStatus,
  HomeDetails,
  IDate,
  InclusiveDateRangeInput,
  LicensingFramework,
  OnBoardingStatus,
  PetPolicyFieldKind,
  Region,
  RevenueModel,
  RiskLevel,
  TargetPartner,
  UUID,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Awaited } from '@avantstay/backoffice-core'

import { BlockPeriod, Period } from '@new/__global/components/TapeChart/TapeChart.types'
import { HomeDatePrices } from '@new/domains/properties/_types'

// eslint-disable-next-line import/no-cycle
import { arriere } from '@arriere/index'

export interface TapeChartRegion
  extends Omit<
    Region,
    'timezone, active, photoUrl, homeCount, coordinates, homesCountingStats, businessEntity'
  > {
  id: string
  name: string
}

// The property object when we refer it inside the Bookings Tape Chart context
export type Property = {
  id: string
  name: string
  details: HomeDetails
  region?: TapeChartRegion
  guestReadinessStatus?: GuestReadinessStatus
  lifecycle?: {
    offboarded?: boolean
    riskLevel?: RiskLevel
  }
  home?: {
    hasPool?: boolean
    roomsCount?: number
    bedroomsCount?: number
  }
}

// The booking object when we refer it inside the Bookings Tape Chart context
// Simple is the one we use when rendering periods so it has less information than the complete Booking
export type SimpleBooking = Awaited<ReturnType<typeof arriere.bookings.getBookingsForTapeChart>>

export type Booking = Awaited<ReturnType<typeof arriere.bookings.getBookingById>>

export interface SelectedRangeProps {
  id: string
  start: string
  end?: string
  tentativeEnd?: string
  minStay?: number
  price?: number
}

export enum BookingsTapeChartViewType {
  reservation = 'RESERVATION',
  payment = 'PAYMENT',
  revenue = 'REVENUE',
}

export interface TapeChartPropertiesFilterProps {
  acquisitionChannel?: AcquisitionChannel[]
  amenityIds?: UUID[]
  checkIn?: IDate
  checkOut?: IDate
  exactBedrooms?: number
  highlightIds?: UUID[]
  homeIds?: string[]
  licensingFramework?: LicensingFramework[]
  maxPrice?: number
  minBathrooms?: number
  minBedrooms?: number
  minOccupancyRate?: number
  maxOccupancyRate?: number
  minPets?: number
  minPrice?: number
  name?: string
  onboardingStatus?: OnBoardingStatus[]
  pagination?: { page: number; size: number }
  petPolicy?: PetPolicyFieldKind
  regionIds?: UUID[]
  revenueModel?: RevenueModel[]
  riskLevel?: RiskLevel[]
  seasonGroupIds?: UUID[]
  targetPartner?: TargetPartner[]
  withOffboarded?: boolean
  occupancyPeriod?: InclusiveDateRangeInput
}

export interface HomesCache {
  data: Property[]
  page: number
  size: number
}

export interface BookingsTapeChartCacheProps {
  homes: HomesCache[]
  homePrices: HomeDatePrices[]
  periods: Period[]
}

export interface BookingsForTapeChartProps {
  filter?: string
  from?: IDate
  to?: IDate
  regionIds?: UUID[]
  homeIds?: UUID[]
  status?: BookingStatus[]
  securityDepositOnHold?: boolean
  paymentBalanceStatus?: BookingPaymentBalanceStatus[]
}

export type TapeChartSidebarFilters = {
  [key in keyof TapeChartSidebarFiltersProps]: TapeChartSidebarFiltersProps[key]
}

export interface TapeChartSidebarFiltersProps {
  acquisitionChannel?: string[]
  amenities?: string[]
  availableDates?: string
  depositsOnHold?: boolean
  exactBedrooms?: string
  highlights?: string[]
  issues?: BookingIssue[]
  licensingFramework?: string[]
  maxPrice?: string
  minBathrooms?: string
  minBedrooms?: string
  minOccupancy?: string
  maxOccupancy?: string
  minPets?: string
  minPrice?: string
  nightlyRate?: boolean
  offBoardedHomes?: boolean
  onboardingStatus?: string[]
  onlyActive?: boolean
  paymentStatus?: string[]
  period?: string
  petsPolicy?: PetPolicyFieldKind
  regions?: string[]
  reservationStatus?: string[]
  revenueModel?: RevenueModel[]
  riskLevel?: RiskLevel[]
  seasonGroupsIds?: string[]
  targetPartner?: string[]
  bedrooms?: string
}

export enum CreateBlockView {
  reservation = 'reservation',
  block = 'block',
  price = 'price',
  quote = 'quote',
}

export interface TapeBlockView extends BlockPeriod {
  periodPrices?: HomeDatePrices[]
  customOpenParentAction?: VoidFunction
}
